import { FileSearch, MessageSquareText, ShieldCheck, Sparkles, X } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { SchemaAgentIntroIllustration } from './SchemaAgentIntroIllustration'

function IntroBenefits() {
  const { t } = useTranslation()
  const benefits = [
    { icon: FileSearch, key: 'files' },
    { icon: MessageSquareText, key: 'discuss' },
    { icon: ShieldCheck, key: 'control' },
  ] as const
  return (
    <ul className="mt-6 grid gap-3 text-xs text-muted-foreground sm:grid-cols-3 sm:gap-5">
      {benefits.map(({ icon: Icon, key }) => (
        <li key={key} className="flex items-center gap-2.5">
          <Icon className="size-4 shrink-0 text-primary-strong" aria-hidden />
          {t(`schemaAgent.intro.benefits.${key}`)}
        </li>
      ))}
    </ul>
  )
}

interface IntroActions {
  dismiss: () => void
  tryNow: () => void
}

function IntroFooter({ actions }: { actions: IntroActions }) {
  const { t } = useTranslation()
  return (
    <div className="mt-8 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-center text-xs text-muted-foreground sm:text-left">
        {t('schemaAgent.intro.reopen', { action: t('schemaAgent.action') })}
      </p>
      <div className="flex items-center justify-center gap-2">
        <Button variant="ghost" onClick={actions.dismiss}>
          {t('schemaAgent.intro.later')}
        </Button>
        <Button
          onClick={actions.tryNow}
          className="h-11 border-primary/30 bg-foreground px-6 text-background shadow-md shadow-primary/10 hover:bg-foreground/90 dark:bg-foreground dark:hover:bg-foreground/90"
        >
          <Sparkles className="size-4" aria-hidden />
          {t('schemaAgent.intro.tryNow')}
        </Button>
      </div>
    </div>
  )
}

function IntroBody({ actions }: { actions: IntroActions }) {
  const { t } = useTranslation()
  return (
    <div className="p-6 sm:p-8">
      <div className="mb-3 flex items-center gap-2.5 text-sm font-medium">
        <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary-strong">
          {t('schemaAgent.intro.new')}
        </Badge>
        {t('schemaAgent.title')}
      </div>
      <DialogTitle className="max-w-xl text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
        {t('schemaAgent.intro.title')}
      </DialogTitle>
      <DialogDescription className="mt-3 max-w-xl text-sm leading-relaxed">
        {t('schemaAgent.intro.description')}
      </DialogDescription>
      <IntroBenefits />
      <IntroFooter actions={actions} />
    </div>
  )
}

export function SchemaAgentIntro({
  open,
  onDismiss,
  onTryNow,
}: {
  open: boolean
  onDismiss: () => void
  onTryNow: () => void
}) {
  const { t } = useTranslation()
  const launching = useRef(false)
  const tryNow = () => {
    launching.current = true
    onDismiss()
    onTryNow()
  }
  return (
    <Dialog open={open} onOpenChange={next => !next && onDismiss()}>
      <DialogContent
        className="max-h-[90dvh] gap-0 overflow-y-auto rounded-2xl p-0 sm:max-w-3xl motion-reduce:animate-none"
        overlayClassName="bg-background/70 supports-backdrop-filter:backdrop-blur-md motion-reduce:animate-none"
        onCloseAutoFocus={event => {
          if (launching.current) event.preventDefault()
          launching.current = false
        }}
      >
        <div className="border-b border-border/60 bg-muted/30 px-3 pt-8 sm:px-8">
          <SchemaAgentIntroIllustration />
        </div>
        <DialogClose asChild>
          <Button
            size="icon-sm"
            variant="ghost"
            className="absolute right-3 top-3 rounded-full text-muted-foreground"
            aria-label={t('common.close')}
          >
            <X className="size-4" aria-hidden />
          </Button>
        </DialogClose>
        <IntroBody actions={{ dismiss: onDismiss, tryNow }} />
      </DialogContent>
    </Dialog>
  )
}
